import { Helmet } from "react-helmet-async";
import { useEffect, useState, useRef } from "react";
import { Typography, Box, Grid, useTheme,Divider } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import { APP_TITLE } from "../../constant";
import AIScoresCard from "../../modules/ViewRecording/AIScoresCard";
import CandidateInfoCard from "../../modules/ViewRecording/CandidateInfoCard";
import ZohoSyncCard from "../../modules/ViewRecording/ZohoSyncCard";
import VideoPlayerCard from "../../modules/ViewRecording/VideoPlayerCard";
import InterviewDetailsCard from "../../modules/ViewRecording/InterviewDetailsCard";
import InterviewQuestionsCard from "../../modules/ViewRecording/InterviewQuestionsCard";
import { format } from "date-fns";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { ArrowLeft } from "../../components/SvgIcons/SvgIcons";
import { AxiosService } from "../../services/axios/axiosService";
// Helper to format seconds as M:SS (e.g. 3:45)
const formatDuration = (seconds) => {
  if (seconds == null || Number.isNaN(Number(seconds))) return "-";
  const total = Math.max(0, Math.floor(Number(seconds)));
  const mins = Math.floor(total / 60);
  const secs = (total % 60).toString().padStart(2, "0");
  return `${mins}:${secs}`;
};

// Determine whether a response should be counted as "answered"
// (mirrors logic in InterviewQuestionsCard/getStatusMeta).
const isResponseAnswered = (response) => {
  if (!response) return false;
  const duration = response.responseDurationSeconds ?? 0;
  const text = response.transcribedText || "";
  const isNoSpeech =
    typeof text === "string" &&
    /(No speech could be recognized|No speech|Recognition timeout)/i.test(
      text,
    );
  const hasSpeech = !isNoSpeech && text.trim().length > 0;
  return duration > 0 && hasSpeech;
};

const ViewRecording = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { state } = useLocation();
  const { id: interviewIdFromUrl } = useParams();
  const [candidateData, setCandidateData] = useState(state?.candidate || null);
  const canGoBack = !!state?.candidate;

  const [detailsData, setDetailsData] = useState({ interview: null, responses: [] });
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState(null);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [ttsAutoPlayKey, setTtsAutoPlayKey] = useState(0);

  // Coordination between TTS and video:
  // - When TTS starts, pause the video
  // - When video starts playing, stop TTS
  const videoPlayerRef = useRef(null);
  const stopTtsRef = useRef(null);

  const candidateInfo = {
    name: candidateData?.candidateName || detailsData.interview?.candidateName || "-",
    email: candidateData?.candidateEmail || detailsData.interview?.candidateEmail || "-",
    position: candidateData?.jobTitle || detailsData.interview?.jobTitle || "-",
    interviewId: candidateData?.id || detailsData.interview?.id || "-",
  };

  const syncStatusData = {
    status: candidateData?.zohoSyncStatus || "Not Synced",
    timestamp: (candidateData?.zohoSyncedAt ? format(new Date(candidateData.zohoSyncedAt), "MM/dd/yyyy") : "-"),
    message:
      "IGNA Scout results and AI scores have been reported to Zoho Recruit.",
  };

  // Figure out which response is active (selected question), or default to
  // the first response when nothing is selected.
  const activeResponse =
    selectedResponse || detailsData.responses?.[0] || null;
  const totalQuestions =
    detailsData.interview?.totalQuestions ?? detailsData.responses?.length ?? 0;
  const answeredCount = Array.isArray(detailsData.responses)
    ? detailsData.responses.filter(isResponseAnswered).length
    : 0;

  // Total duration across all recorded questions (sum of each response's duration)
  const totalDurationSeconds = Array.isArray(detailsData.responses)
    ? detailsData.responses.reduce(
        (sum, r) => sum + (r?.responseDurationSeconds ?? 0),
        0,
      )
    : 0;

  // AI scores to display:
  // - For a specific question (activeResponse), use behavioralScore/contentScore
  // - Fallback to overall candidate interview scores (behaviourScore/answereScore)
  const questionScores = activeResponse
    ? {
        behaviorScore:
          activeResponse.behaviorScore ??
          activeResponse.behaviourScore ??
          activeResponse.behavioralScore ??
          null,
        aiResponse:
          activeResponse.aiResponse ??
          activeResponse.answereScore ??
          activeResponse.contentScore ??
          null,
        contentFeedback:
          activeResponse.contentFeedback ??
          null,
          behavioralFeedback:
          activeResponse.behavioralNotes ??
          null,
      }
    : null;

  const overallScores = candidateData
    ? {
        behaviorScore:
          candidateData.behaviorScore ??
          candidateData.behaviourScore ??
          candidateData.behavioralScore ??
          null,
        aiResponse:
          candidateData.aiResponse ??
          candidateData.answereScore ??
          candidateData.contentScore ??
          null,
      }
    : null;

  const scoresData = questionScores || overallScores || {};

  const totalBehaviourScore =
    candidateData?.behaviourScore ??
    candidateData?.behaviorScore ??
    candidateData?.behavioralScore;
  const totalAiScore =
    candidateData?.aiResponse ??
    candidateData?.answereScore ??
    candidateData?.contentScore;

  const interviewDetails = {
    date: candidateData?.completedAt
      ? format(new Date(candidateData.completedAt), "MM/dd/yyyy")
      : "-",
    duration: formatDuration(totalDurationSeconds),
    questionsAnswered:
      totalQuestions > 0
        ? `${answeredCount} of ${totalQuestions}`
        : answeredCount > 0
          ? `${answeredCount} answered`
          : "0 of 0",
    totalBehaviourScore:
      totalBehaviourScore != null
        ? Math.floor(Number(totalBehaviourScore) * 10) / 10
        : null,
    totalAiScore:
      totalAiScore != null ? Math.floor(Number(totalAiScore) * 10) / 10 : null,
    interviewId: candidateData?.id || interviewIdFromUrl || "-",
  };

  const resolvedInterviewId = candidateData?.id || interviewIdFromUrl;

  // Text to show under the video player.
  // When there are no responses at all, show an explicit "no question" message
  // instead of leaving this area blank.
  const videoQuestionText =
    selectedResponse?.question?.questionText ||
    (!questionsLoading &&
      !questionsError &&
      (detailsData.responses?.length ?? 0) === 0
      ? "No question details available."
      : "");

  const transcriptText =
    activeResponse?.transcribedText && typeof activeResponse.transcribedText === "string"
      ? activeResponse.transcribedText
      : "";
  const hasVideoForActive =
    !!activeResponse?.videoBlobUrl &&
    typeof activeResponse.videoBlobUrl === "string" &&
    activeResponse.videoBlobUrl.trim() !== "";

  // When TTS starts (question audio), pause any playing video
  const handleTtsStart = () => {
    const api = videoPlayerRef.current;
    if (api && typeof api.pause === "function") {
      api.pause();
    }
  };

  // When video starts playing, stop any TTS audio
  const handleVideoPlay = () => {
    if (typeof stopTtsRef.current === "function") {
      stopTtsRef.current();
    }
  };

  useEffect(() => {
    const interviewId = resolvedInterviewId;
    if (!interviewId) return;

    const fetchInterviewDetails = async () => {
      try {
        setQuestionsLoading(true);
        setQuestionsError(null);

        const response = await AxiosService.get(
          `/api/Interview/${interviewId}/details`,
        );

        const responses = response?.responses ?? [];
        setDetailsData({
          interview: response?.interview ?? null,
          responses,
        });

        // If opened directly via URL (no navigation state),
        // populate candidateData from the API response.
        if (!candidateData && response?.interview) {
          setCandidateData(response.interview);
        }

        if (responses.length > 0) {
          setSelectedResponse(responses[0]);
        } else {
          setSelectedResponse(null);
        }
      } catch (err) {
        console.error(err);
        setQuestionsError(
          err?.message || "Failed to load interview questions.",
        );
      } finally {
        setQuestionsLoading(false);
      }
    };

    fetchInterviewDetails();
  }, [resolvedInterviewId]);

  // Shared-link mode (no navigation state):
  // when user clicks browser Back, refresh this same page.
  useEffect(() => {
    if (canGoBack) return;

    const currentUrl = window.location.href;

    // Add one extra entry so the first Back triggers `popstate` while still on this page.
    window.history.pushState(null, "", currentUrl);

    const handlePopState = () => {
      // Keep the user on the same URL and refresh the page content.
      window.history.pushState(null, "", currentUrl);
      window.location.reload();
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [canGoBack]);

  return (
    <>
      <Helmet>
        <title>Interview Recording | {APP_TITLE}</title>
        <meta
          name="Interview Recording"
          content="Review candidate interview and AI-generated scores"
        />
      </Helmet>

      <Box
        sx={{
          p: 4,
          backgroundColor: theme.palette.background.default,
          minHeight: "100vh",
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              mb: 0.5,
            }}
          >
            {canGoBack && (
              <Box
                onClick={() => navigate(-1)}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  padding: "8px",
                  borderRadius: "8px",
                  transition: "all 0.2s",
                  "&:hover": {
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(255, 255, 255, 0.05)"
                        : "#f3f4f6",
                  },
                }}
              >
                <ArrowLeft size={24} color={theme.palette.text.primary} />
              </Box>
            )}
            <Typography
              variant="h5"
              sx={{ fontWeight: 600, color: theme.palette.text.primary }}
            >
              IGNA Scout Recording
            </Typography>
          </Box>       
        </Box>

        {/* Top question strip in a card */}
        {/* <Box sx={{ mb: 3 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 2,
              border: `1px solid ${theme.palette.divider}`,
              backgroundColor: theme.palette.background.paper,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0px 1px 3px rgba(0,0,0,0.4)"
                  : "0px 1px 3px rgba(15,23,42,0.12)",
            }}
          >
            <CardContent sx={{ py: 1.5, px: 2.5 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mb: 1,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: 600, color: theme.palette.text.primary }}
                >
                  Questions:
                </Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 1.5,
                }}
              >
                {(detailsData.responses || []).map((resp, index) => {
              const answered = isResponseAnswered(resp);
              const hasAnyResponse =
                (resp?.responseDurationSeconds ?? 0) > 0 ||
                (resp?.transcribedText &&
                  resp.transcribedText.toString().trim().length > 0);
              const isCurrent =
                activeResponse && resp?.id === activeResponse.id;

              const baseButtonSx = {
                width: 36,
                height: 32,
                borderRadius: 1.2,
                fontSize: 13,
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                background: "transparent",
                border: "1px solid #e5e7eb",
                color: theme.palette.text.primary,
              };

              let buttonSx = baseButtonSx;

              if (answered) {
                buttonSx = {
                  ...baseButtonSx,
                  borderColor: "#16a34a",
                  bgcolor: "#f0fdf4",
                  color: "#166534",
                };
              } else if (hasAnyResponse) {
                buttonSx = {
                  ...baseButtonSx,
                  borderColor: "#dc2626",
                  bgcolor: "#fef2f2",
                  color: "#b91c1c",
                };
              } else if (isCurrent) {
                buttonSx = {
                  ...baseButtonSx,
                  borderColor: theme.palette.primary.main,
                  bgcolor: theme.palette.primary.main,
                  color: "#ffffff",
                };
              }

                return (
                  <Box
                    key={resp?.id ?? index}
                    sx={{ position: "relative" }}
                  >
                    <Box
                      component="button"
                      type="button"
                      onClick={() => setSelectedResponse(resp)}
                      sx={{
                        ...buttonSx,
                        cursor: "pointer",
                        outline: "none",
                      }}
                    >
                      {index + 1}
                    </Box>
                    {answered && (
                      <CheckCircleIcon
                        sx={{
                          position: "absolute",
                          top: -6,
                          right: -6,
                          fontSize: 16,
                          color: "#16a34a",
                          bgcolor: "#ffffff",
                          borderRadius: "50%",
                          boxShadow: "0 0 0 1px rgba(22,163,74,0.2)",
                        }}
                      />
                    )}
                    {!answered && hasAnyResponse && (
                      <CloseIcon
                        sx={{
                          position: "absolute",
                          top: -6,
                          right: -6,
                          fontSize: 16,
                          color: "#dc2626",
                          bgcolor: "#ffffff",
                          borderRadius: "50%",
                          boxShadow: "0 0 0 1px rgba(220,38,38,0.2)",
                        }}
                      />
                    )}
                  </Box>
                );
              })}
              </Box>
            </CardContent>
          </Card>
        </Box> */}

        <Grid container spacing={{ xs: 2, md: 3 }} alignItems="stretch">
          {/* IGNA Scout details + Candidate info + AI scores + Zoho side by side */}
          <Grid size={{ xs: 12, md: 6, lg: 3 }} sx={{ minWidth: 0 }}>
          <CandidateInfoCard candidate={candidateInfo} />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }} sx={{ minWidth: 0 }}>
          <InterviewDetailsCard details={interviewDetails} />
            
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }} sx={{ minWidth: 0 }}>
            <AIScoresCard scores={scoresData} />
          </Grid>
          <Grid size={{ xs: 12, md: 6, lg: 3 }} sx={{ minWidth: 0 }}>
            <ZohoSyncCard syncStatus={syncStatusData} />
          </Grid>

           {/* Video + details section (always middle in DOM order) */}
           <Grid size={{ xs: 12, md: 6, sm:12, lg: 6 }} sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                minWidth: 0,
              }}
            >
              <Box sx={{ flex: 1, mb: 2 }}>
                <VideoPlayerCard
                  interview={interviewDetails}
                  ref={videoPlayerRef}
                  videoBlobUrl={activeResponse?.videoBlobUrl}
                  questionText={videoQuestionText}
                  transcriptText={transcriptText}
                  autoPlayKey={ttsAutoPlayKey}
                  onVideoPlay={handleVideoPlay}
                  loading={questionsLoading}
                  showNoVideo={!questionsLoading && !questionsError && !hasVideoForActive}
                />
              </Box>
             
            </Box>
          </Grid>
          {/* Questions section (always left in DOM order) */}
          <Grid size={{ xs: 12, md: 6, lg: 6}} sx={{ display: "flex", minWidth: 0 }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                flex: 1,
                minHeight: 0,
                minWidth: 0,
              }}
            >
              <InterviewQuestionsCard
                loading={questionsLoading}
                error={questionsError}
                questions={detailsData.responses}
                totalQuestions={detailsData.interview?.totalQuestions}
                selectedResponseId={selectedResponse?.id}
                onSelectResponse={setSelectedResponse}
                onTtsEnd={() => setTtsAutoPlayKey((k) => k + 1)}
                onTtsStart={handleTtsStart}
                registerStopTts={(fn) => {
                  stopTtsRef.current = fn;
                }}
              />
            </Box>
          </Grid>

         
         
        </Grid>
      </Box>
    </>
  );
};

export default ViewRecording;
